/**
 * Performance monitoring utilities
 * Tracks Core Web Vitals, query performance, and route load times
 */

import { onCLS, onINP, onFCP, onLCP, onTTFB, Metric } from 'web-vitals';
import { logger } from './logger';

interface PerformanceMetrics {
  cls: number | null;
  inp: number | null;
  fcp: number | null;
  lcp: number | null;
  ttfb: number | null;
  routeLoadTimes: Map<string, number>;
  slowQueries: Array<{ queryKey: string; duration: number; timestamp: number }>;
}

const metrics: PerformanceMetrics = {
  cls: null,
  inp: null,
  fcp: null,
  lcp: null,
  ttfb: null,
  routeLoadTimes: new Map(),
  slowQueries: [],
};

/**
 * Initialize Core Web Vitals tracking
 */
export function initPerformanceMonitoring() {
  if (typeof window === 'undefined') return;

  // Track Core Web Vitals
  onCLS((metric: Metric) => {
    metrics.cls = metric.value;
    if (metric.value > 0.25) {
      logger.warn('[Performance] Poor CLS', { value: metric.value, rating: metric.rating });
    } else {
      logger.info('[Performance] CLS', { value: metric.value, rating: metric.rating });
    }
  });

  onINP((metric: Metric) => {
    metrics.inp = metric.value;
    if (metric.value > 200) {
      logger.warn('[Performance] Poor INP', { value: metric.value, rating: metric.rating });
    } else {
      logger.info('[Performance] INP', { value: metric.value, rating: metric.rating });
    }
  });

  onFCP((metric: Metric) => {
    metrics.fcp = metric.value;
    if (metric.value > 3000) {
      logger.warn('[Performance] Poor FCP', { value: metric.value, rating: metric.rating });
    } else {
      logger.info('[Performance] FCP', { value: metric.value, rating: metric.rating });
    }
  });

  onLCP((metric: Metric) => {
    metrics.lcp = metric.value;
    if (metric.value > 4000) {
      logger.warn('[Performance] Poor LCP', { value: metric.value, rating: metric.rating });
    } else {
      logger.info('[Performance] LCP', { value: metric.value, rating: metric.rating });
    }
  });

  onTTFB((metric: Metric) => {
    metrics.ttfb = metric.value;
    if (metric.value > 800) {
      logger.warn('[Performance] Poor TTFB', { value: metric.value, rating: metric.rating });
    } else {
      logger.info('[Performance] TTFB', { value: metric.value, rating: metric.rating });
    }
  });

  // Track route load times
  if ('performance' in window && 'PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            const route = window.location.pathname;
            const loadTime = navEntry.loadEventEnd - navEntry.fetchStart;
            metrics.routeLoadTimes.set(route, loadTime);
            
            if (loadTime > 3000) {
              logger.warn('[Performance] Slow route load', { route, loadTime: `${loadTime.toFixed(0)}ms` });
            }
          }
        }
      });
      observer.observe({ entryTypes: ['navigation'] });
    } catch (e) {
      logger.warn('[Performance] PerformanceObserver not supported', { error: e });
    }
  }
}

/**
 * Track query execution time
 */
export function trackQueryPerformance(queryKey: string[], startTime: number) {
  const duration = performance.now() - startTime;
  
  if (duration > 1000) {
    metrics.slowQueries.push({
      queryKey: queryKey.join('/'),
      duration,
      timestamp: Date.now(),
    });
    
    // Keep only last 50 slow queries
    if (metrics.slowQueries.length > 50) {
      metrics.slowQueries.shift();
    }
    
    logger.warn('[Performance] Slow query', {
      queryKey: queryKey.join('/'),
      duration: `${duration.toFixed(0)}ms`,
    });
  }
}

/**
 * Get current performance metrics
 */
export function getPerformanceMetrics(): PerformanceMetrics {
  return {
    ...metrics,
    routeLoadTimes: new Map(metrics.routeLoadTimes),
    slowQueries: [...metrics.slowQueries],
  };
}

/**
 * Performance budget configuration (targets for Lighthouse)
 */
export const PERFORMANCE_BUDGET = {
  fcp: 1800, // 1.8s
  lcp: 2500, // 2.5s
  fid: 100, // 100ms (INP replaces FID in modern metrics)
  inp: 200, // 200ms
  cls: 0.1, // 0.1
  ttfb: 600, // 600ms
  tti: 3800, // 3.8s
  bundleSize: 500 * 1024, // 500KB gzipped
} as const;

/**
 * Capture basic performance metrics from Performance API
 */
export function capturePerformanceMetrics(): {
  fcp: number;
  lcp: number;
  ttfb: number;
  domInteractive: number;
} | null {
  if (typeof window === 'undefined' || !window.performance) return null;

  const navigation = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  const paint = performance.getEntriesByType('paint');

  const fcp =
    paint.find((entry) => entry.name === 'first-contentful-paint')?.startTime ?? 0;

  return {
    fcp,
    lcp: 0, // Requires PerformanceObserver for LCP
    ttfb: navigation ? navigation.responseStart - navigation.requestStart : 0,
    domInteractive: navigation?.domInteractive ?? 0,
  };
}

/**
 * Log performance summary
 */
export function logPerformanceSummary() {
  const summary = {
    coreWebVitals: {
      cls: metrics.cls,
      inp: metrics.inp,
      fcp: metrics.fcp,
      lcp: metrics.lcp,
      ttfb: metrics.ttfb,
    },
    routeCount: metrics.routeLoadTimes.size,
    slowQueryCount: metrics.slowQueries.length,
  };
  
  logger.info('[Performance] Summary', summary);
  
  return summary;
}

